/**
 * AnnouncementsPage — 全局公告：当前生效 + 历史列表
 */

import { useCallback, useEffect, useState } from "react";
import {
  archiveAdminAnnouncement,
  createAdminAnnouncement,
  deleteAdminAnnouncement,
  getAdminCurrentAnnouncement,
  listAdminAnnouncements,
  publishAdminAnnouncement,
  unpublishAdminAnnouncement,
  updateAdminAnnouncement,
  type Announcement,
} from "../api/adminClient";

const inputClass =
  "w-full px-4 py-2.5 border border-[var(--color-outline-variant)] rounded-lg text-sm bg-[var(--color-surface)] text-[var(--color-on-surface)] placeholder:text-[var(--color-outline)] focus:outline-none focus:border-[var(--color-accent-blue)] focus:ring-1 focus:ring-[var(--color-accent-blue)] transition-colors transition-shadow duration-150";

const thClass = "pb-3 pr-4 font-medium text-left text-[var(--color-on-surface-variant)]";
const tdClass = "py-3 pr-4 text-[var(--color-on-surface)] align-middle";

function fmtDate(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    const raw = String(iso);
    const d = new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function StatusBadge({ status }: { status: string }) {
  if (status === "published") {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-600">
        生效中
      </span>
    );
  }
  if (status === "draft") {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-700">
        草稿
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded text-xs font-medium bg-[var(--color-surface-container-low)] text-[var(--color-on-surface-variant)]">
      已归档
    </span>
  );
}

interface Props {
  onAuthError?: () => void;
}

export default function AnnouncementsPage({ onAuthError }: Props) {
  const [current, setCurrent] = useState<Announcement | null>(null);
  const [items, setItems] = useState<Announcement[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [editId, setEditId] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const handleAuth = (msg: string) => {
    if (msg.includes("401") || msg.includes("403") || msg.includes("管理员")) {
      onAuthError?.();
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cur, list] = await Promise.all([
        getAdminCurrentAnnouncement(),
        listAdminAnnouncements({
          page,
          limit: 20,
          status: statusFilter || undefined,
        }),
      ]);
      setCurrent(cur.announcement ?? null);
      setItems(list.items ?? []);
      setPages(list.pages ?? 1);
      setTotal(list.total ?? 0);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      handleAuth(msg);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, onAuthError]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setEditId(null);
    setTitle("");
    setBody("");
  };

  const startEdit = (a: Announcement) => {
    setEditId(a.id);
    setTitle(a.title || "");
    setBody(a.body || "");
    setMessage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startNew = () => {
    resetForm();
    setMessage(null);
  };

  const run = async (fn: () => Promise<void>) => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
      await load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      handleAuth(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () =>
    run(async () => {
      if (!title.trim() && !body.trim()) {
        throw new Error("标题与正文不能都为空");
      }
      if (editId != null) {
        const res = await updateAdminAnnouncement(editId, {
          title: title.trim(),
          body: body.trim(),
        });
        setMessage(res.message);
      } else {
        const res = await createAdminAnnouncement({
          title: title.trim(),
          body: body.trim(),
          publish: false,
        });
        setMessage(res.message);
        setEditId(res.announcement.id);
      }
    });

  const handlePublish = () =>
    run(async () => {
      if (!body.trim()) {
        throw new Error("发布时正文不能为空");
      }
      if (
        !window.confirm(
          "确认发布？若已有生效公告，将自动归档旧公告，官网与客户端将显示本条。",
        )
      ) {
        return;
      }
      if (editId != null) {
        await updateAdminAnnouncement(editId, {
          title: title.trim(),
          body: body.trim(),
        });
        const res = await publishAdminAnnouncement(editId);
        setMessage(res.message);
        setEditId(res.announcement.id);
      } else {
        const res = await createAdminAnnouncement({
          title: title.trim(),
          body: body.trim(),
          publish: true,
        });
        setMessage(res.message);
        setEditId(res.announcement.id);
      }
    });

  const handleUnpublish = (id: number) =>
    run(async () => {
      if (!window.confirm("确认下线当前公告？官网与客户端将不再显示。")) return;
      const res = await unpublishAdminAnnouncement(id);
      setMessage(res.message);
      if (editId === id) resetForm();
    });

  const handleRepublish = (id: number) =>
    run(async () => {
      if (
        !window.confirm(
          "确认再次发布该公告？当前生效公告（如有）将被归档。",
        )
      ) {
        return;
      }
      const res = await publishAdminAnnouncement(id);
      setMessage(res.message);
    });

  const handleArchive = (id: number) =>
    run(async () => {
      const res = await archiveAdminAnnouncement(id);
      setMessage(res.message);
      if (editId === id) resetForm();
    });

  const handleDelete = (id: number) =>
    run(async () => {
      if (!window.confirm("确认永久删除该公告？此操作不可恢复。")) return;
      const res = await deleteAdminAnnouncement(id);
      setMessage(res.message);
      if (editId === id) resetForm();
    });

  return (
    <div className="animate-page-enter space-y-6">
      <div className="mb-2">
        <h1
          className="text-[28px] font-semibold text-[var(--color-on-surface)] mb-1"
          style={{ letterSpacing: "-0.02em" }}
        >
          公告管理
        </h1>
        <p className="text-[15px] text-[var(--color-on-surface-variant)]">
          同时最多一条生效公告，展示于官网与客户端。发布新公告会自动归档旧公告。
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {message && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 px-4 py-3 text-sm">
          {message}
        </div>
      )}

      {/* 当前公告 */}
      <section className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-sm font-semibold text-[var(--color-on-surface)]">当前公告</h2>
          <button
            type="button"
            onClick={startNew}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-[var(--color-accent-blue)] text-white"
          >
            新建公告
          </button>
        </div>
        {loading && !current ? (
          <p className="text-sm text-[var(--color-on-surface-variant)]">加载中…</p>
        ) : current ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={current.status} />
              <span className="text-xs text-[var(--color-on-surface-variant)]">
                发布于 {fmtDate(current.published_at)}
                {current.updated_at && current.updated_at !== current.published_at
                  ? ` · 更新于 ${fmtDate(current.updated_at)}`
                  : ""}
              </span>
            </div>
            {current.title ? (
              <h3 className="text-base font-semibold text-[var(--color-on-surface)]">
                {current.title}
              </h3>
            ) : null}
            <p className="text-sm text-[var(--color-on-surface)] whitespace-pre-wrap leading-relaxed">
              {current.body}
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                disabled={saving}
                onClick={() => startEdit(current)}
                className="px-3 py-1.5 rounded-lg text-sm border border-[var(--color-outline-variant)]"
              >
                编辑
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleUnpublish(current.id)}
                className="px-3 py-1.5 rounded-lg text-sm border border-red-200 text-red-600"
              >
                下线
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-on-surface-variant)]">
            暂无生效公告。可在下方编写并发布。
          </p>
        )}
      </section>

      {/* 编辑表单 */}
      <section className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-6">
        <h2 className="text-sm font-semibold text-[var(--color-on-surface)] mb-4">
          {editId != null ? `编辑公告 #${editId}` : "新建公告"}
        </h2>
        <div className="space-y-4 max-w-2xl">
          <div>
            <label className="block text-xs font-medium text-[var(--color-on-surface-variant)] mb-1.5">
              标题（可选，最长 120 字）
            </label>
            <input
              className={inputClass}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例如：系统维护通知"
              maxLength={120}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-on-surface-variant)] mb-1.5">
              正文（发布时必填，最长 2000 字）
            </label>
            <textarea
              className={inputClass}
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="公告内容，支持换行"
              maxLength={2000}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSaveDraft()}
              className="px-4 py-2 rounded-lg text-sm border border-[var(--color-outline-variant)] disabled:opacity-60"
            >
              {saving ? "保存中…" : editId != null ? "保存修改" : "保存为草稿"}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void handlePublish()}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-accent-blue)] text-white disabled:opacity-60"
            >
              {editId != null ? "保存并发布" : "直接发布"}
            </button>
            {editId != null && (
              <button
                type="button"
                disabled={saving}
                onClick={startNew}
                className="px-4 py-2 rounded-lg text-sm text-[var(--color-on-surface-variant)]"
              >
                取消编辑
              </button>
            )}
          </div>
        </div>
      </section>

      {/* 历史 / 列表 */}
      <section className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-sm font-semibold text-[var(--color-on-surface)]">
            全部公告
            <span className="ml-2 text-xs font-normal text-[var(--color-on-surface-variant)]">
              共 {total} 条
            </span>
          </h2>
          <select
            className={inputClass + " w-auto sm:w-40"}
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value);
            }}
          >
            <option value="">全部状态</option>
            <option value="published">生效中</option>
            <option value="draft">草稿</option>
            <option value="archived">已归档</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border-subtle)]">
                <th className={thClass}>状态</th>
                <th className={thClass}>标题 / 摘要</th>
                <th className={thClass}>发布时间</th>
                <th className={thClass}>归档时间</th>
                <th className={thClass}>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-[var(--color-on-surface-variant)]">
                    {loading ? "加载中…" : "暂无记录"}
                  </td>
                </tr>
              ) : (
                items.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-[var(--color-border-subtle)]/60 last:border-0"
                  >
                    <td className={tdClass}>
                      <StatusBadge status={a.status} />
                    </td>
                    <td className={tdClass}>
                      <div className="font-medium max-w-xs truncate">
                        {a.title || `公告 #${a.id}`}
                      </div>
                      <div className="text-xs text-[var(--color-on-surface-variant)] max-w-sm truncate mt-0.5">
                        {a.body}
                      </div>
                    </td>
                    <td className={tdClass + " whitespace-nowrap text-xs"}>
                      {fmtDate(a.published_at)}
                    </td>
                    <td className={tdClass + " whitespace-nowrap text-xs"}>
                      {fmtDate(a.archived_at)}
                    </td>
                    <td className={tdClass}>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          className="text-xs text-[var(--color-accent-blue)] hover:underline"
                          onClick={() => startEdit(a)}
                        >
                          编辑
                        </button>
                        {a.status !== "published" && (
                          <button
                            type="button"
                            className="text-xs text-emerald-600 hover:underline"
                            disabled={saving}
                            onClick={() => void handleRepublish(a.id)}
                          >
                            发布
                          </button>
                        )}
                        {a.status === "published" && (
                          <button
                            type="button"
                            className="text-xs text-amber-700 hover:underline"
                            disabled={saving}
                            onClick={() => void handleUnpublish(a.id)}
                          >
                            下线
                          </button>
                        )}
                        {a.status === "draft" && (
                          <button
                            type="button"
                            className="text-xs text-[var(--color-on-surface-variant)] hover:underline"
                            disabled={saving}
                            onClick={() => void handleArchive(a.id)}
                          >
                            归档
                          </button>
                        )}
                        {a.status !== "published" && (
                          <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            disabled={saving}
                            onClick={() => void handleDelete(a.id)}
                          >
                            删除
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg text-sm border border-[var(--color-outline-variant)] disabled:opacity-40"
            >
              上一页
            </button>
            <span className="text-xs text-[var(--color-on-surface-variant)]">
              {page} / {pages}
            </span>
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-lg text-sm border border-[var(--color-outline-variant)] disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
